import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ChevronLeft, ChevronRight, TrendingUp, DollarSign, Receipt, ArrowDown, ArrowUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DailyReport() {
  const token = localStorage.getItem('vendor_token');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);

  const fetchReport = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/business/daily?date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
    setData(await res.json());
  }, [date, token]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const changeDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  if (!data) return <div className="flex items-center justify-center h-64 text-slate-500">Loading...</div>;

  const stats = [
    { label: 'Bills', value: data.total_bills, icon: Receipt, color: 'text-blue-600 bg-blue-50' },
    { label: 'Revenue', value: `Rs.${data.total_revenue?.toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Received', value: `Rs.${data.total_received?.toLocaleString()}`, icon: ArrowDown, color: 'text-green-600 bg-green-50' },
    { label: 'Discount', value: `Rs.${data.total_discount?.toLocaleString()}`, icon: DollarSign, color: 'text-amber-600 bg-amber-50' },
    { label: 'Due', value: `Rs.${data.total_due?.toLocaleString()}`, icon: ArrowUp, color: 'text-red-600 bg-red-50' },
    { label: 'Expenses', value: `Rs.${data.total_expenses?.toLocaleString()}`, icon: ArrowUp, color: 'text-orange-600 bg-orange-50' },
    { label: 'Net Income', value: `Rs.${data.net_income?.toLocaleString()}`, icon: TrendingUp, color: 'text-purple-600 bg-purple-50' },
  ];

  return (
    <div data-testid="lis-daily-report">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">Daily Business Report</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => changeDate(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-[160px] h-9" />
          <Button variant="outline" size="sm" onClick={() => changeDate(1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {stats.map((s, i) => { const Icon = s.icon; return (
          <Card key={`card-${i}`} className="border-slate-200"><CardContent className="p-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${s.color}`}><Icon className="w-4 h-4" /></div>
            <p className="text-[10px] text-slate-500">{s.label}</p>
            <p className="text-sm font-bold text-slate-800">{s.value}</p>
          </CardContent></Card>
        ); })}
      </div>

      {Object.keys(data.payment_modes || {}).length > 0 && (
        <Card className="mb-4 border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Payment Modes</CardTitle></CardHeader><CardContent>
          <div className="flex gap-3 flex-wrap">{Object.entries(data.payment_modes).map(([mode, amt]) => (
            <Badge key={mode} variant="outline" className="text-sm px-3 py-1">{mode}: Rs.{amt.toLocaleString()}</Badge>
          ))}</div>
        </CardContent></Card>
      )}

      {data.bills?.length > 0 && (
        <Card className="mb-4 border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Bills ({data.bills.length})</CardTitle></CardHeader><CardContent>
          <table className="w-full text-xs"><thead><tr className="border-b bg-slate-50"><th className="p-2 text-left">Reg#</th><th className="p-2 text-left">Patient</th><th className="p-2 text-left">Referred</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Paid</th><th className="p-2 text-center">Status</th></tr></thead>
            <tbody>{data.bills.map(b => (
              <tr key={b.id} className="border-b border-slate-100"><td className="p-2">#{b.reg_no}</td><td className="p-2">{b.patient_name}</td><td className="p-2 text-slate-500">{b.referred_by || '-'}</td><td className="p-2 text-right">Rs.{b.total}</td><td className="p-2 text-right text-emerald-600">Rs.{b.amount_received}</td><td className="p-2 text-center"><Badge className={b.status === 'no_due' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{b.status === 'no_due' ? 'Paid' : 'Due'}</Badge></td></tr>
            ))}</tbody>
          </table>
        </CardContent></Card>
      )}
    </div>
  );
}
